import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FiCheck, FiFileText, FiUploadCloud } from 'react-icons/fi';
import DepositContract from '../components/DepositContract';
import ContractExportActions from '../components/ContractExportActions';
import DepositSignaturePad from '../components/DepositSignaturePad';
import { authService, depositFormService, roomService } from '../api/services';
import { createIdentityReader } from '../utils/identityReader';
import { calculateAdditionalDeposit, emptyRoomFields, getDepositRoomFields } from '../utils/depositRoom';
import { dayAfter, getDepositDateError } from '../utils/depositDates';
import useDepositDraft from '../hooks/useDepositDraft';
import useCustomerDraftWarning from '../hooks/useCustomerDraftWarning';

const initialForm = {
  fullName: '',
  phone: '',
  dob: '',
  gender: '',
  zalo: '',
  cccd: '',
  issueDate: '',
  issuePlace: '',
  permanentAddress: '',
  hometown: '',
  productId: '',
  roomCode: '',
  projectAddress: '',
  rent: '',
  rentalMonths: '',
  checkInDate: '',
  occupants: '',
  vehicles: '',
  electricity: '',
  water: '',
  parking: '',
  management: '',
  otherFee: '',
  totalDeposit: '',
  depositAmount: '',
  depositDate: '',
  paymentMethod: '',
  bankAccountNumber: '',
  bankName: '',
  bankAccountName: '',
  additionalDeposit: '',
  additionalDepositDate: '',
  representativeName: '', representativePhone: '', representativeCode: '',
  contractSignDate: '', tenantSignature: '', representativeSignature: '',
};

const getEndDate = (startDate, months) => {
  if (!startDate || !months) {
    return '';
  }
  const date = new Date(`${startDate}T00:00:00`);
  date.setMonth(date.getMonth() + Number(months));
  date.setDate(date.getDate() - 1);
  return date.toLocaleDateString('vi-VN');
};

const ImageUpload = ({ label, file, onChange, required = false, disabled = false }) => {
  const [preview, setPreview] = useState('');
  useEffect(() => {
    if (!file) { setPreview(''); return undefined; }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  return (
    <label className="deposit-image-upload">
      <span className="deposit-field-label">{label}{required && <b>*</b>}</span>
      <span className="deposit-image-box">
        {preview ? <img src={preview} alt={label} /> : <><FiUploadCloud size={26} /><span>Tải ảnh lên</span></>}
      </span>
      <input type="file" disabled={disabled} accept="image/jpeg,image/png,image/webp" onChange={(event) => {
        onChange(event.target.files?.[0] || null);
        event.target.value = '';
      }} />
    </label>
  );
};

const CreateDeposit = () => {
  const [form, setForm] = useState(() => {
    let user;
    try { user = authService?.getCurrentUser?.(); } catch { user = null; }
    return { ...initialForm, representativeName: user?.name || '', representativePhone: user?.phone || '', representativeCode: user?.user_code || user?.employee_code || '' };
  });
  const [idFront, setIdFront] = useState(null);
  const [idBack, setIdBack] = useState(null);
  const [paymentProof, setPaymentProof] = useState(null);
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const [previewReady, setPreviewReady] = useState(false);
  const [ocrStatus, setOcrStatus] = useState('');
  const [readingIdentity, setReadingIdentity] = useState(false);
  const [scanAttempt, setScanAttempt] = useState(0);
  const identityReaderRef = useRef(null);
  const lastScanAttemptRef = useRef(0);
  if (!identityReaderRef.current) identityReaderRef.current = createIdentityReader();
  useEffect(() => () => identityReaderRef.current.cancel(), []);
  const autoValuesRef = useRef({});
  const editVersionsRef = useRef({});
  const [roomStatus, setRoomStatus] = useState('');
  const resolvedRoomRef = useRef(null);
  const contractPreviewRef = useRef(null);
  const restoredRoomRef = useRef('');
  const restoredImagesRef = useRef(null);
  const draftFiles = useMemo(() => ({ id_front: idFront, id_back: idBack, payment_proof: paymentProof }), [idFront, idBack, paymentProof]);
  const draft = useDepositDraft({
    userId: authService.getCurrentUser()?.id, form, files: draftFiles,
    restore: (saved, files, metadata) => {
      restoredRoomRef.current = saved.productId || '';
      if (metadata?.roomId) resolvedRoomRef.current = { productId: String(saved.productId || '').trim(), id: metadata.roomId };
      restoredImagesRef.current = files;
      setForm((previous) => ({ ...previous, ...saved }));
      setIdFront(files.id_front || null); setIdBack(files.id_back || null); setPaymentProof(files.payment_proof || null);
      setPreviewReady(false);
    },
  });
  const customerDrafts = useCustomerDraftWarning({ userId: authService.getCurrentUser()?.id, currentId: draft.id, ready: draft.ready, completed: draft.completed, phone: form.phone, cccd: form.cccd });

  useEffect(() => {
    const id = form.productId.trim();
    if (restoredRoomRef.current === id && id) return undefined;
    if (!id) { setRoomStatus(''); return undefined; }
    if (!/^(?:[1-9]\d*|ID-[A-Z0-9]{20})$/i.test(id)) {
      setRoomStatus('Nhập mã sản phẩm dạng ID-... trên tin đăng hoặc ID phòng dạng số.');
      return undefined;
    }
    const controller = new AbortController();
    const versions = { ...editVersionsRef.current };
    setRoomStatus('Đang tải thông tin phòng...');
    const timer = setTimeout(async () => {
      try {
        const room = await roomService.getRoom(id, controller.signal);
        if (controller.signal.aborted) return;
        resolvedRoomRef.current = { productId: id, id: room.id };
        const fields = getDepositRoomFields(room);
        setForm((previous) => {
          const next = { ...previous, tenantSignature: '', representativeSignature: '', ...Object.fromEntries(Object.entries(fields).filter(([key]) => versions[key] === editVersionsRef.current[key])) };
          next.totalDeposit = fields.totalDeposit;
          next.additionalDeposit = calculateAdditionalDeposit(next.totalDeposit, next.depositAmount);
          if (!(Number(next.additionalDeposit) > 0)) next.additionalDepositDate = '';
          return next;
        });
        setPreviewReady(false);
        setRoomStatus('Đã điền thông tin từ tin đăng. Các ô còn trống cần nhập thêm; bạn có thể sửa lại thông tin.');
      } catch (error) {
        if (!controller.signal.aborted) {
          setRoomStatus(error.response?.status === 404
            ? 'Không tìm thấy phòng với ID này. Vui lòng kiểm tra lại ID sản phẩm.'
            : 'Không tải được thông tin phòng. Vui lòng nhập lại ID để thử lại hoặc nhập thông tin thủ công.');
        }
      }
    }, 350);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [form.productId]);

  useEffect(() => {
    if (!idFront && !idBack) return undefined;
    const controller = new AbortController();
    const versions = { ...editVersionsRef.current };
    const force = scanAttempt !== lastScanAttemptRef.current;
    if (!force && restoredImagesRef.current?.id_front === idFront && restoredImagesRef.current?.id_back === idBack) return undefined;
    lastScanAttemptRef.current = scanAttempt;
    const timer = setTimeout(async () => {
      setReadingIdentity(true);
      setOcrStatus('Đang đọc thông tin CCCD...');
      const sideData = {};
      const applyResult = (side, data) => {
        if (controller.signal.aborted) return;
        sideData[side] = data;
        const extracted = { ...sideData.back, ...sideData.front };
        const fields = ['fullName', 'dob', 'gender', 'cccd', 'issueDate', 'issuePlace', 'permanentAddress', 'hometown'];
        const allowed = Object.fromEntries(fields.filter((key) => extracted?.[key] && versions[key] === editVersionsRef.current[key]).map((key) => [key, extracted[key]]));
        const previousAuto = autoValuesRef.current;
        setForm((previous) => {
          const next = { ...previous, tenantSignature: '', representativeSignature: '' };
          Object.entries(allowed).forEach(([key, value]) => {
            if (!previous[key] || previous[key] === previousAuto[key]) next[key] = value;
          });
          return next;
        });
        autoValuesRef.current = { ...previousAuto, ...allowed };
        setPreviewReady(false);
      };
      try {
        const jobs = [[idFront, 'front'], [idBack, 'back']].filter(([file]) => file).map(([file, side]) => (
          identityReaderRef.current.read(file, side, force).then((data) => { applyResult(side, data); return data; })
        ));
        const results = await Promise.allSettled(jobs);
        if (controller.signal.aborted) return;
        const failure = results.find((result) => result.status === 'rejected');
        const extracted = { ...sideData.back, ...sideData.front };
        if (failure) {
          const reason = failure.reason?.response?.data?.message || 'Dịch vụ đang phản hồi chậm hoặc quá tải. Vui lòng thử lại sau.';
          setOcrStatus(Object.keys(extracted).length ? `Đã điền thông tin đọc được. Một mặt CCCD chưa đọc được: ${reason}` : reason);
          return;
        }
        setOcrStatus(Object.keys(extracted || {}).length
          ? 'Đã đọc CCCD. Vui lòng kiểm tra các thông tin tự điền. Ngày cấp và nơi cấp cần ảnh mặt sau; số Zalo nhập thủ công.'
          : 'Chưa đọc được thông tin. Hãy chọn ảnh rõ hơn hoặc nhập thủ công.');
      } catch (error) {
        if (!controller.signal.aborted) setOcrStatus(error.response?.data?.message || 'Không thể đọc CCCD. Vui lòng thử lại hoặc nhập thủ công.');
      } finally {
        if (!controller.signal.aborted) setReadingIdentity(false);
      }
    }, 100);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [idFront, idBack, scanAttempt]);

  const selectIdentityImage = (side, file) => {
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type) || file.size > 5 * 1024 * 1024) {
      setOcrStatus('Vui lòng chọn ảnh JPG, PNG hoặc WebP có dung lượng không quá 5MB.');
      return;
    }
    if (side === 'front') setIdFront(file); else setIdBack(file);
    setPreviewReady(false);
  };

  const endDate = useMemo(
    () => getEndDate(form.checkInDate, form.rentalMonths),
    [form.checkInDate, form.rentalMonths],
  );

  const update = (field, value) => {
    if (field === 'paymentMethod') setPaymentProof(null);
    if (field === 'productId') { resolvedRoomRef.current = null; restoredRoomRef.current = ''; }
    editVersionsRef.current[field] = (editVersionsRef.current[field] || 0) + 1;
    delete autoValuesRef.current[field];
    setForm((previous) => {
      const next = { ...previous, tenantSignature: '', representativeSignature: '', ...(field === 'productId' ? emptyRoomFields : {}), [field]: value };
      if (['productId', 'totalDeposit', 'depositAmount'].includes(field)) {
        next.additionalDeposit = calculateAdditionalDeposit(next.totalDeposit, next.depositAmount);
        if (!(Number(next.additionalDeposit) > 0)) next.additionalDepositDate = '';
      }
      if (field === 'paymentMethod') {
        next.bankAccountNumber = '';
        next.bankName = '';
        next.bankAccountName = '';
      }
      return next;
    });
    setPreviewReady(false);
  };

  const validateForPreview = () => {
    const dateError = getDepositDateError(form);
    if (!form.roomCode.trim() || dateError) {
      setMessage(!form.roomCode.trim() ? 'Vui lòng nhập Mã Phòng.' : dateError);
      setPreviewReady(false);
      return false;
    }
    if (!idFront || !form.fullName || !form.phone || !form.productId || !form.checkInDate || !form.depositAmount) {
      setMessage('Vui lòng nhập đủ họ tên, số điện thoại, ID phòng, ngày nhận phòng, tiền cọc và ảnh CCCD mặt trước.');
      setPreviewReady(false);
      return false;
    }
    setMessage('');
    setPreviewReady(true);
    return true;
  };

  const input = (field, label, options = {}) => (
    <label className={`deposit-field ${options.className || ''}`}>
      <span className="deposit-field-label">{label}{options.required && <b>*</b>}</span>
      <input
        type={options.type || 'text'}
        value={form[field]}
        placeholder={options.placeholder || ''}
        min={options.min}
        required={options.required}
        readOnly={options.readOnly}
        disabled={options.disabled || draft.completed}
        maxLength={options.maxLength}
        onChange={(event) => update(field, event.target.value)}
      />
    </label>
  );

  const select = (field, label, values, options = {}) => (
    <label className={`deposit-field ${options.className || ''}`}>
      <span className="deposit-field-label">{label}</span>
      <select value={form[field]} disabled={draft.completed} onChange={(event) => update(field, event.target.value)}>
        <option value="">{options.placeholder || 'Chọn...'}</option>
        {values.map((value) => <option key={value} value={value}>{value}</option>)}
      </select>
    </label>
  );

  const buildNotes = () => [
    `Họ tên: ${form.fullName}`,
    `Số điện thoại: ${form.phone}`,
    `Ngày sinh: ${form.dob}`,
    `Giới tính: ${form.gender}`,
    `Zalo: ${form.zalo}`,
    `CCCD số: ${form.cccd}`,
    `Ngày cấp: ${form.issueDate}`,
    `Nơi cấp: ${form.issuePlace}`,
    `Nơi thường trú: ${form.permanentAddress}`,
    `Quê quán: ${form.hometown}`,
    `Mã sản phẩm: ${form.productId}`,
    `Mã phòng: ${form.roomCode}`,
    `Địa chỉ dự án: ${form.projectAddress}`,
    `Giá thuê: ${form.rent}`,
    `Số tháng: ${form.rentalMonths}`,
    `Ngày dự kiến Checkin: ${form.checkInDate}`,
    `Số người ở: ${form.occupants}`,
    `Số lượng xe: ${form.vehicles}`,
    `Phí điện: ${form.electricity}`,
    `Phí nước: ${form.water}`,
    `Phí giữ xe: ${form.parking}`,
    `Phí quản lý: ${form.management}`,
    `Phí khác: ${form.otherFee}`,
    `Số tiền cọc: ${form.totalDeposit}`,
    `Cọc giữ phòng: ${form.depositAmount}`,
    `Ngày cọc: ${form.depositDate}`,
    `Phương thức cọc: ${form.paymentMethod}`,
    ...(form.paymentMethod === 'Chuyển khoản' ? [`Số tài khoản: ${form.bankAccountNumber}`, `Ngân hàng: ${form.bankName}`, `Tên tài khoản: ${form.bankAccountName}`] : []),
    `Cọc bổ sung: ${form.additionalDeposit}`,
    `Ngày cọc bổ sung: ${form.additionalDepositDate}`,
  ].join('\n');

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (draft.completed || saving) return;
    const dateError = getDepositDateError(form);
    if (!form.roomCode.trim() || dateError) {
      setMessage(!form.roomCode.trim() ? 'Vui lòng nhập Mã Phòng.' : dateError);
      return;
    }
    if (!idFront || !form.productId || !form.fullName || !form.phone || !form.checkInDate) {
      setMessage('Vui lòng nhập các trường có dấu * đỏ, bao gồm ảnh CCCD mặt trước.');
      return;
    }

    setSaving(true);
    setMessage('');
    try {
      const draftId = await draft.beginFinalize();
      const data = new FormData();
      if (draftId) data.append('draft_id', draftId);
      const productId = form.productId.trim();
      const roomId = resolvedRoomRef.current?.productId === productId
        ? resolvedRoomRef.current.id
        : (await roomService.getRoom(productId)).id;
      if (!roomId) throw new Error('Room ID could not be resolved');
      data.append('room_id', String(roomId));
      data.append('start_date', form.checkInDate);
      data.append('form_data', JSON.stringify(form));
      data.append('notes', buildNotes());
      if (idFront) {
        data.append('id_front', idFront);
      }
      if (idBack) {
        data.append('id_back', idBack);
      }
      if (paymentProof && form.paymentMethod) data.append('payment_proof', paymentProof);
      await depositFormService.createDeposit(data);
      await draft.complete();
      setMessage(draft.editingId ? 'Đã cập nhật phiếu cọc thành công và chuyển sang trạng thái chờ duyệt.' : 'Đã tạo phiếu cọc thành công và chuyển sang trạng thái chờ duyệt.');
    } catch (error) {
      draft.resume();
      setMessage(error.response?.data?.message || 'Không thể tạo phiếu cọc. Vui lòng kiểm tra Mã sản phẩm.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="container deposit-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">{draft.editingId ? `Chỉnh sửa phiếu cọc #${draft.editingId}` : 'Tạo phiếu cọc'}</h1>
          <p className="page-subtitle">{draft.editingId ? 'Thay đổi được tự lưu thành bản nháp. Khi bấm Lưu thay đổi, phiếu hiện tại sẽ được cập nhật và chuyển về Chờ duyệt.' : 'Nhập đầy đủ thông tin khách hàng, phòng và thỏa thuận đặt cọc.'}</p>
        </div>
      </div>
      {draft.loadError && <p role="alert" className="error-message">{draft.loadError} <a href={`#/create-deposit?new=${Date.now()}`}>Tạo phiếu mới</a></p>}
      {!draft.ready && !draft.loadError && <p role="status">Đang khôi phục bản nháp…</p>}
      {draft.status && <div className="deposit-draft-status" aria-live="polite">{draft.status} {!draft.completed && <button type="button" className="btn btn-neutral btn-sm" onClick={draft.retry}>Thử lưu lại</button>} <a href="#/bookings">Xem phiếu cọc</a></div>}
      {draft.completed && <p className="success-message">Đã hoàn thành phiếu cọc. <a href={`#/create-deposit?new=${Date.now()}`}>Tạo phiếu mới</a></p>}
      <form onSubmit={handleSubmit}>
        <fieldset className="deposit-editor-fields" disabled={!draft.ready || saving}>
        <section className="deposit-section">
          <h2>1. Thông tin CCCD khách hàng</h2>
          <div className="deposit-image-grid">
            <ImageUpload label="Ảnh mặt trước" disabled={draft.completed} required file={idFront} onChange={(file) => selectIdentityImage('front', file)} />
            <ImageUpload label="Ảnh mặt sau" disabled={draft.completed} file={idBack} onChange={(file) => selectIdentityImage('back', file)} />
          </div>
          <p className="page-subtitle">Ảnh CCCD được gửi tới Gemini để tự điền họ tên và các thông tin trên thẻ. Bạn có thể kiểm tra và sửa lại sau khi tự điền.</p>
          {ocrStatus && <p role="status" aria-live="polite">{ocrStatus}</p>}
          {(idFront || idBack) && <button type="button" className="btn btn-secondary" disabled={readingIdentity || draft.completed} onClick={() => setScanAttempt((value) => value + 1)}>{readingIdentity ? 'Đang đọc CCCD...' : 'Đọc lại CCCD'}</button>}
          {customerDrafts.matches.length > 0 && <div className="alert alert-warning customer-draft-warning" role="status" aria-live="polite">
            <strong>Khách hàng này đang có {customerDrafts.matches.length} phiếu cọc chưa hoàn thành.</strong>
            <p>Bạn có thể mở bản nháp để tiếp tục nhập thông tin.</p>
            {customerDrafts.matches.map((item) => <a className="btn btn-neutral btn-sm" key={item.id} href={`#/create-deposit?draft=${item.id}`}>
              Mở bản nháp{item.form_data?.roomCode ? ` · Phòng ${item.form_data.roomCode}` : ''}
            </a>)}
          </div>}
          {customerDrafts.unavailable && <p className="muted-text">Chưa kiểm tra được hết bản nháp trên hệ thống. Thông tin bạn đang nhập vẫn được tự lưu.</p>}
          <div className="deposit-grid deposit-grid-3">
            {input('fullName', 'Họ và tên', { required: true, placeholder: 'Nhập hoặc tải CCCD để tự điền họ tên' })}
            {input('phone', 'Số điện thoại chính', { required: true })}
            {input('dob', 'Ngày sinh', { type: 'date' })}
            {select('gender', 'Giới tính', ['Nam', 'Nữ', 'Khác'])}
            {input('zalo', 'Số điện thoại Zalo')}
            {input('cccd', 'CCCD số')}
            {input('issueDate', 'Ngày cấp', { type: 'date' })}
            {input('issuePlace', 'Nơi cấp')}
            {input('permanentAddress', 'Nơi thường trú', { className: 'span-2' })}
            {input('hometown', 'Quê quán')}
          </div>
        </section>

        <section className="deposit-section">
          <h2>2. Phiếu giữ cọc phòng</h2>
          <div className="deposit-subsection">
            <h3>Thông tin phòng</h3>
            {roomStatus && <p role="status" aria-live="polite">{roomStatus}</p>}
            <div className="deposit-grid deposit-grid-4">
              {input('productId', 'ID Sản phẩm', { required: true, placeholder: 'Nhập mã ID-... hoặc ID phòng' })}
              {input('roomCode', 'Mã Phòng', { required: true })}
              {input('projectAddress', 'Địa chỉ dự án', { className: 'span-3' })}
              {input('rent', 'Giá thuê (VNĐ)', { type: 'number', min: 0 })}
              {input('rentalMonths', 'Thời hạn (Tháng)', { type: 'number', min: 1 })}
              {input('checkInDate', 'Ngày dự kiến Checkin', { type: 'date', required: true, min: form.depositDate || undefined })}
              {input('occupants', 'Số người ở', { type: 'number', min: 1 })}
              {input('vehicles', 'Số lượng xe', { type: 'number', min: 0 })}
            </div>
          </div>
          <div className="deposit-subsection">
            <h3>Phí dịch vụ</h3>
            <div className="deposit-grid deposit-grid-2">
              {input('electricity', 'Phí điện', { placeholder: 'Chọn hoặc nhập...' })}
              {input('water', 'Phí nước', { placeholder: 'Chọn hoặc nhập...' })}
              {input('parking', 'Phí giữ xe', { placeholder: 'Chọn hoặc nhập...' })}
              {input('management', 'Phí quản lý', { placeholder: 'Chọn hoặc nhập...' })}
              {input('otherFee', 'Phí khác')}
            </div>
          </div>
          <div className="deposit-subsection">
            <h3>Tiền cọc</h3>
            <div className="deposit-grid deposit-grid-3">
              {input('totalDeposit', 'Số tiền cọc (VNĐ)', { type: 'number', min: 0, readOnly: true, placeholder: 'Tự lấy từ phòng đã chọn' })}
              {input('depositAmount', 'Cọc giữ phòng (VNĐ)', { type: 'number', min: 0 })}
              {input('depositDate', 'Ngày cọc', { type: 'date' })}
              {select('paymentMethod', 'Phương thức cọc', ['Chuyển khoản', 'Tiền mặt'])}
              {input('additionalDeposit', 'Cọc bổ sung (VNĐ)', { type: 'number', min: 0, readOnly: true })}
              {input('additionalDepositDate', 'Ngày cọc bổ sung', { type: 'date', min: dayAfter(form.depositDate), disabled: !(Number(form.additionalDeposit) > 0) })}
            </div>
            {getDepositDateError(form) && <p role="alert" className="error-message">{getDepositDateError(form)}</p>}
          </div>
          {form.paymentMethod && <div className="deposit-subsection">
            <h3>{form.paymentMethod === 'Chuyển khoản' ? 'Thông tin chuyển khoản' : 'Thanh toán tiền mặt'}</h3>
            {form.paymentMethod === 'Chuyển khoản' && <div className="deposit-grid deposit-grid-3">
              {input('bankAccountNumber', 'Số tài khoản', { placeholder: 'Nhập số tài khoản' })}
              {input('bankName', 'Ngân hàng', { placeholder: 'Nhập tên ngân hàng' })}
              {input('bankAccountName', 'Tên tài khoản', { placeholder: 'Nhập tên chủ tài khoản' })}
            </div>}
            <div className="deposit-image-grid">
              <ImageUpload disabled={draft.completed} label={form.paymentMethod === 'Chuyển khoản' ? 'Hình chuyển khoản' : 'Hình ảnh số tiền'} file={paymentProof} onChange={(file) => {
                if (!file) return;
                if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type) || file.size > 5 * 1024 * 1024) {
                  setMessage('Vui lòng chọn ảnh JPG, PNG hoặc WebP không quá 5MB.');
                  return;
                }
                setPaymentProof(file);
                setPreviewReady(false);
                setMessage('');
              }} />
            </div>
          </div>}
        </section>

        <section className="deposit-section">
          <h2>3. Hợp đồng đặt cọc</h2>
          <div className="deposit-subsection">
            <h3>Đại diện bộ phận kinh doanh BigSix</h3>
            <div className="deposit-grid deposit-grid-3">
              {input('representativeName', 'Họ tên đại diện kinh doanh', { maxLength: 100 })}
              {input('representativePhone', 'Số điện thoại đại diện', { maxLength: 30 })}
              {input('representativeCode', 'Mã nhân viên', { maxLength: 50 })}
              {input('contractSignDate', 'Ngày ký hợp đồng', { type: 'date' })}
            </div>
          </div>
          {!previewReady ? (
          <div className="deposit-contract-empty">
            <FiFileText size={34} />
            <strong>Chưa tạo hợp đồng cọc</strong>
            <span>Vui lòng bấm nút bên dưới để kiểm tra dữ liệu và tạo bản xem trước 2 trang A4.</span>
            <button type="button" className="btn btn-primary" onClick={validateForPreview}>
              <FiCheck size={16} /> Kiểm tra dữ liệu
            </button>
          </div>
          ) : (
          <div className="deposit-contract-preview" ref={contractPreviewRef}>
            <DepositContract form={form} />
            <ContractExportActions previewRef={contractPreviewRef} roomCode={form.roomCode} onEdit={() => setPreviewReady(false)} />
          </div>
          )}
          <div className="deposit-signatures">
            <DepositSignaturePad label="Ký tên (Khách hàng)" value={form.tenantSignature} onChange={(value) => { if (!draft.completed) setForm((previous) => ({ ...previous, tenantSignature: value })); }} />
            <DepositSignaturePad label="Ký tên (Đại diện kinh doanh BigSix)" value={form.representativeSignature} onChange={(value) => { if (!draft.completed) setForm((previous) => ({ ...previous, representativeSignature: value })); }} />
          </div>
        </section>

        <div className="deposit-actions">
          <span className="deposit-end-date">{endDate ? `Ngày kết thúc dự kiến: ${endDate}` : ''}</span>
          <button type="submit" className="btn btn-primary" disabled={saving || draft.completed}>{saving ? 'Đang lưu...' : draft.editingId ? 'Lưu thay đổi' : 'Lưu phiếu cọc'}</button>
        </div>
        {message && <div className="form-message">{message}</div>}
        </fieldset>
      </form>
    </main>
  );
};

export default CreateDeposit;
